import { useState } from "react";
import { ArrowUpRight, Handshake, TrendingUp, Wrench } from "lucide-react";
import { scrollToSection } from "@/hooks/useLenis";
import { useContent } from "@/content/ContentContext";
import { trackEvent } from "@/lib/analytics";
import { DealerInquiryForm } from "./DealerInquiryForm";
import { Eyebrow, Reveal } from "./Reveal";

const PERKS = [
    {
        icon: TrendingUp,
        title: "A new revenue lane",
        copy: "Earn on every subscription sold from your showroom, F&I office, or service drive — month after month, not once at the desk.",
    },
    {
        icon: Wrench,
        title: "Your counter stays the hero",
        copy: "Claims land back in your own service department at your retail door rate. Repairs stay in-house, and customers stay yours.",
    },
    {
        icon: Handshake,
        title: "Built by your peers",
        copy: "Designed by dealership service directors, so it's a contract your advisors can hand across the counter without wincing.",
    },
];

export const DealerStrip = () => {
    const [formOpen, setFormOpen] = useState(false);
    const content = useContent();

    return (
        <>
            <section
                id="dealers"
                data-testid="dealers-section"
                className="relative overflow-hidden py-24 md:py-36"
            >
        <div
            className="absolute right-[-10%] top-0 h-[440px] w-[440px] rounded-full bg-sky-500/[0.08] blur-[130px]"
            aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
                <div className="relative overflow-hidden rounded-[2.5rem] border border-sky-400/20 bg-[#0C1019] p-8 md:p-14 lg:p-16">
                    <div
                        className="bg-grid absolute inset-0 opacity-60"
                        aria-hidden="true"
                    />
                    <div className="relative grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
                        <div className="lg:col-span-6">
                            <Eyebrow>{content.dealers_eyebrow}</Eyebrow>
                            <h2 className="mt-5 font-display text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-5xl">
                                {content.dealers_heading_a}{" "}
                                <span className="text-sky-400">
                                    {content.dealers_heading_b}
                                </span>
                            </h2>
                            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-400 md:text-lg">
                                {content.dealers_subtext}
                            </p>
                            <div className="mt-9 flex flex-wrap items-center gap-4">
                                <button
                                    data-testid="dealer-partner-btn"
                                    onClick={() => {
                                        trackEvent("dealer_form_open");
                                        setFormOpen(true);
                                    }}
                                    className="group inline-flex items-center gap-2.5 rounded-full bg-sky-400 px-7 py-4 text-base font-bold text-[#0A0D14] transition-[background-color,box-shadow,transform] duration-300 hover:bg-sky-300 hover:shadow-[0_0_44px_rgba(56,189,248,0.5)] active:scale-95"
                                >
                                    Become a Partner Dealer
                                    <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </button>
                                <button
                                    data-testid="dealer-coverage-btn"
                                    onClick={() => scrollToSection("#plans")}
                                    className="rounded-full border border-white/15 px-7 py-4 text-base font-semibold text-white transition-[border-color,background-color] duration-300 hover:border-sky-400/50 hover:bg-white/5"
                                >
                                    See the coverage
                                </button>
                            </div>
                            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                                Created by dealers. Sold by dealers. Honored by
                                dealers.
                            </p>
                        </div>

                        <div className="flex flex-col gap-5 lg:col-span-6">
                            {PERKS.map((p, i) => (
                                <Reveal key={p.title} delay={0.1 + i * 0.1}>
                                    <div
                                        data-testid={`dealer-perk-${i}`}
                                        className="group flex items-start gap-5 rounded-2xl border border-white/10 bg-[#121722] p-6 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-sky-400/40"
                                    >
                                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-400/10 text-sky-400 transition-colors duration-300 group-hover:bg-sky-400 group-hover:text-[#0A0D14]">
                                            <p.icon className="h-5 w-5" />
                                        </span>
                                        <div>
                                            <h3 className="font-display text-lg font-semibold tracking-tight text-white">
                                                {p.title}
                                            </h3>
                                            <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
                                                {p.copy}
                                            </p>
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </Reveal>
        </div>
            </section>
            <DealerInquiryForm open={formOpen} onOpenChange={setFormOpen} />
        </>
    );
};
